/*************************************************************************
* ADOBE CONFIDENTIAL
* ___________________
*
* Copyright 2021 Adobe
* All Rights Reserved.
*
* NOTICE: All information contained herein is, and remains
* the property of Adobe and its suppliers, if any. The intellectual
* and technical concepts contained herein are proprietary to Adobe
* and its suppliers and are protected by all applicable intellectual
* property laws, including trade secret and copyright laws.
* Dissemination of this information or reproduction of this material
* is strictly forbidden unless prior written permission is obtained
* from Adobe.
**************************************************************************/
import {useSelector, useDispatch} from 'react-redux';
import {getEnvironment} from '../../../utils/Environment';
import NotificationAnalytics from '../../../utils/NotificationAnalytics';
import LocalStorageUtils from '../../../utils/LocalStorageUtils';
import AnalyticsUtil from '../../../utils/AnalyticsUtil';
import {getLocalizedURL} from '@intl/adobe-locales';
import {CREATE_NOTIFICATION} from '../../types';
import {createNotificationPayload} from '../Notification/Notification';

const PAYMENT_STATUSES = {
	PAID: 'PAID',
	PENDING_PAYMENT: 'PENDING_PAYMENT',
	GRACE_PAST_DUE: 'GRACE_PAST_DUE',
	PAST_DUE: 'PAST_DUE',
	CANCELLING: 'CANCELLING',
	CANCELLED: 'CANCELLED',
	INACTIVE: 'INACTIVE',
};

const PAYMENT_FAILURE_CLOSED_STORAGE_KEY = 'home_components:payment_failure_notification_closed';

interface PaymentFailureProps {
	intl: any;
	locale: any;
}

/**
 * Method to fetch status of payment failure
 *
 */
const PaymentFailure: React.FC<PaymentFailureProps> = ({locale, intl}) => {
	const gqlProfile = useSelector(state => state.profile.gqlProfile);
	const notifications = useSelector(state => state.appNotifications.notifications);
	const dispatch = useDispatch();
	const notificationPresent = notifications.filter(notification => notification.name === PAYMENT_FAILURE_CLOSED_STORAGE_KEY);
	if (notificationPresent.length) {
		return null;
	}

	const paidSubs = gqlProfile.account.subscriptions.paid;
	const ls = new LocalStorageUtils();

	let editPaymentUrl = '';
	let paymentStatus = '';
	let shouldDispatchNotification = false;
	if (paidSubs && !ls.getItem(PAYMENT_FAILURE_CLOSED_STORAGE_KEY)) {
		const filterdSubs = paidSubs.filter(subscription => (subscription.paymentStatus.toUpperCase() === PAYMENT_STATUSES.GRACE_PAST_DUE) || (subscription.paymentStatus.toUpperCase() === PAYMENT_STATUSES.PAST_DUE));
		if (filterdSubs.length) {
			shouldDispatchNotification = true;
			paymentStatus = filterdSubs[0].contractStatus;
			const planId = filterdSubs[0].planId.toUpperCase();
			editPaymentUrl = getEnvironment() === 'prod' ? `https://account.adobe.com/plans/${planId}/edit-payment` : `https://stage.account.adobe.com/plans/${planId}/edit-payment`;
		}
	}

	const handleOnAction = () => {
		ls.setItem(PAYMENT_FAILURE_CLOSED_STORAGE_KEY, true);
		const analytics = new NotificationAnalytics();
		analytics.postPaymentFailureNotificationAction(paymentStatus, 'update');

		let appendedUrl = getLocalizedURL(editPaymentUrl, locale);
		appendedUrl = AnalyticsUtil.appendUrlWithAnalytics(appendedUrl);
		open(appendedUrl, '_blank');
	};

	const handleOnClose = () => {
		ls.setItem(PAYMENT_FAILURE_CLOSED_STORAGE_KEY, true);
		const analytics = new NotificationAnalytics();
		analytics.postPaymentFailureNotificationAction(paymentStatus, 'dismiss');
	};

	if (!ls.getItem(PAYMENT_FAILURE_CLOSED_STORAGE_KEY) && (shouldDispatchNotification === true)) {
		dispatch({
			type: CREATE_NOTIFICATION,
			payload: createNotificationPayload(`${PAYMENT_FAILURE_CLOSED_STORAGE_KEY}`, intl.formatMessage({id: 'app.notification.paymentFailure'}), {
				closable: true,
				variant: 'error',
				actionLabel: intl.formatMessage({id: 'app.notification.paymentFailureCTA'}),
				onCloseCallback: handleOnClose,
				onActionCallback: handleOnAction,
			}),
		});
	}

	return null;
};

export default PaymentFailure;

export {PAYMENT_FAILURE_CLOSED_STORAGE_KEY};
